/**
 * The play-screen MAP PANEL (Big Phase 1.5 UI): a collapsible inline mini-map
 * beside the journal, plus a "View maps" control that opens the SAME map in a
 * full-screen modal (the map library / full viewer). Both share the one
 * {@link CampaignMap} surface (zoom/pan, Z-library, markers, you-are-here), so
 * behavior is identical small or large.
 *
 * Degrades quietly: the map read is best-effort inside `CampaignMap`, so a
 * failure just shows "no map yet" and never disturbs play.
 */

import { useEffect, useState } from "react";

import { CampaignMap, type SpatialMapApi } from "./CampaignMap.js";

export interface MapPanelProps {
  readonly api: SpatialMapApi;
  readonly campaignId: string;
  /** Bumped by the host (e.g. after each turn) to refetch the map for live deltas. */
  readonly refreshKey?: number;
}

export function MapPanel({ api, campaignId, refreshKey = 0 }: MapPanelProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);

  // Close the full-screen viewer on Escape.
  useEffect(() => {
    if (!fullOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullOpen]);

  return (
    <section className="map-panel" aria-label="Map" data-testid="map-panel">
      <div className="map-panel-header">
        <h2 className="map-panel-title">Map</h2>
        <div className="map-panel-actions">
          <button
            type="button"
            data-testid="map-toggle-collapse"
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? "Show map" : "Hide map"}
          </button>
          <button type="button" data-testid="map-open-full" onClick={() => setFullOpen(true)}>
            View maps
          </button>
        </div>
      </div>

      {!collapsed ? (
        <div className="map-panel-inline" data-testid="map-panel-inline">
          <CampaignMap api={api} campaignId={campaignId} variant="inline" refreshKey={refreshKey} />
        </div>
      ) : null}

      {fullOpen ? (
        <div
          className="map-fullscreen-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Maps"
          data-testid="map-fullscreen"
          // No app stylesheet exists, so the overlay's layout is set inline: a
          // fixed full-viewport box as a flex column. The definite height is what
          // lets the full map's `flex:1` viewport actually resolve to a size
          // (without it, flex:1 would collapse to 0 and the big map wouldn't show).
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            background: "#f3e9d2",
          }}
        >
          <div className="map-fullscreen-chrome">
            <button
              type="button"
              className="map-fullscreen-close"
              data-testid="map-fullscreen-close"
              aria-label="Close maps"
              onClick={() => setFullOpen(false)}
            >
              ✕ Close
            </button>
          </div>
          <CampaignMap api={api} campaignId={campaignId} variant="full" refreshKey={refreshKey} />
        </div>
      ) : null}
    </section>
  );
}
